import { Helmet } from "react-helmet-async";

const SITE_URL = "https://yoursolicitor.co.uk";

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "LegalService",
  "@id": `${SITE_URL}/#organization`,
  "name": "YourSolicitor.co.uk",
  "alternateName": "BlueLion Claims",
  "legalName": "BlueLion Law Limited",
  "url": SITE_URL,
  "logo": `${SITE_URL}/logo.png`,
  "description": "Expert no win, no fee solicitors specialising in PCP and HP car finance mis-selling compensation claims across the United Kingdom.",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "The Centro Buildings, 20–23 Mandela Street",
    "addressLocality": "London",
    "postalCode": "NW1 0DU",
    "addressCountry": "GB"
  },
  "telephone": "+442081530033",
  "email": "contactus@bluelionclaims.co.uk",
  "areaServed": {
    "@type": "Country",
    "name": "United Kingdom"
  },
  "serviceType": "Car Finance Mis-Selling Claims",
  "hasCredential": [
    {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "SRA Regulated",
      "description": "Regulated by the Solicitors Regulation Authority (SRA no. 815166)"
    }
  ],
  "knowsAbout": [
    "PCP Car Finance Claims",
    "HP Car Finance Claims",
    "Discretionary Commission Arrangements",
    "FCA Consumer Redress Scheme",
    "Hidden Commission Compensation",
    "No Win No Fee Legal Services"
  ],
  "sameAs": [
    "https://yoursolicitor.co.uk"
  ]
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  "url": SITE_URL,
  "name": "YourSolicitor.co.uk",
  "description": "PCP and HP car finance mis-selling claims experts. No Win, No Fee.",
  "publisher": {
    "@id": `${SITE_URL}/#organization`
  },
  "inLanguage": "en-GB"
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${SITE_URL}/#webpage`,
  "url": SITE_URL,
  "name": "YourSolicitor.co.uk | PCP & HP Car Finance Claims — No Win, No Fee",
  "description": "Were you mis-sold PCP or HP car finance? Check if you could claim compensation in under 60 seconds. No Win, No Fee solicitors.",
  "isPartOf": { "@id": `${SITE_URL}/#website` },
  "about": { "@id": `${SITE_URL}/#organization` },
  "inLanguage": "en-GB",
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": SITE_URL
      }
    ]
  }
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is No Win, No Fee? Can I claim back myself?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Our service is provided on a No Win, No Fee basis. You only pay our fees if your claim is successful. You can cancel without charge within the 14-day cooling-off period. For claims that settle through the FCA's Consumer Redress Scheme, our fees are capped as per our regulator's rules. The FCA estimates the average claim value will be around £830 per eligible agreement. You are also free to complain directly to your lender at no cost, or escalate to the Financial Ombudsman Service, which is free of charge."
      }
    },
    {
      "@type": "Question",
      "name": "What is mis-sold car finance?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Mis-sold car finance typically relates to Discretionary Commission Arrangements (DCAs), where car dealers could set the interest rate on your finance agreement and earn more commission for higher rates. Many consumers paid more than they should have without being told about this conflict of interest. The FCA banned DCAs in January 2021. Consumers affected between April 2007 and October 2024 may be entitled to compensation."
      }
    },
    {
      "@type": "Question",
      "name": "How much does a PCP car finance claim cost?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We operate on a No Win, No Fee basis — if your claim is unsuccessful, you pay nothing. If we win, our success fee is a maximum of 36% (inclusive of VAT) of the compensation recovered for claims settled without litigation. For claims settled through litigation, the maximum deduction is 48% inclusive of VAT. You have a 14-day cooling-off period to cancel without charge."
      }
    },
    {
      "@type": "Question",
      "name": "How much is the average car finance mis-selling claim?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Financial Conduct Authority (FCA) has indicated that under its Consumer Redress Scheme, eligible claims could average around £830 per finance agreement. Actual compensation varies depending on the interest rate charged, the length of your agreement, and the total amount financed. The total compensation scheme is expected to cost lenders up to £7.5 billion."
      }
    },
    {
      "@type": "Question",
      "name": "Can I make more than one PCP or HP finance claim?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. You can make one claim per finance agreement. If you took out multiple cars on finance between April 2007 and October 2024, you could be eligible for multiple separate claims."
      }
    },
    {
      "@type": "Question",
      "name": "What if I do not have my car finance paperwork?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "You do not need any paperwork to get started. We can help you find your finance agreements and request the necessary documentation directly from the lender on your behalf. All you need is your basic personal details."
      }
    },
    {
      "@type": "Question",
      "name": "Will making a car finance claim affect my credit score?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No, making a claim will not affect your credit score. The claim is against the lender for hidden commission, not a dispute about your ability to repay the loan. Your credit history and score remain completely unaffected throughout the claims process."
      }
    },
    {
      "@type": "Question",
      "name": "Can I claim if I have already paid off my car finance?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. You can still claim even if you have fully paid off your car finance. As long as your finance agreement was active between April 2007 and October 2024, you may be entitled to compensation for hidden commission charges."
      }
    },
    {
      "@type": "Question",
      "name": "Can I claim car finance compensation myself without a solicitor?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, you do not have to use a solicitor or claims management company. You can complain directly to your lender at no cost, or escalate to the Financial Ombudsman Service for free. However, many people prefer to use professional expertise to navigate the process and maximise their compensation."
      }
    }
  ]
};

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Make a PCP Car Finance Mis-Selling Claim",
  "description": "Start your no win, no fee PCP or HP car finance claim in three simple steps.",
  "totalTime": "PT3M",
  "estimatedCost": {
    "@type": "MonetaryAmount",
    "currency": "GBP",
    "value": "0",
    "description": "No upfront cost — No Win, No Fee"
  },
  "step": [
    {
      "@type": "HowToStep",
      "position": 1,
      "name": "Complete our free online check",
      "text": "Answer a few quick questions about your car finance to see if you could have a valid claim. Takes less than 60 seconds."
    },
    {
      "@type": "HowToStep",
      "position": 2,
      "name": "We investigate your claim",
      "text": "Our expert solicitors review your finance agreements and gather evidence from lenders on your behalf — no paperwork needed from you."
    },
    {
      "@type": "HowToStep",
      "position": 3,
      "name": "Receive your compensation",
      "text": "If your claim is successful, you receive your compensation directly. Our fee is only charged on success."
    }
  ]
};

export default function StructuredData() {
  const schemas = [organizationSchema, websiteSchema, webPageSchema, faqSchema, howToSchema];
  return (
    <Helmet>
      {schemas.map((schema) => (
        <script key={schema['@type']} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
